import React, { useEffect, useState } from "react";
import RegistrationLinkListing from "./RegistrationLinkListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setPage,
  setRowsPerPage,
  setSearchValue,
  setItems,
  setTotalItems,
  setIsTableLoading,
} from "src/redux/slices/RegistrationLinkSlice";
import { MdOutlinePublishedWithChanges } from "react-icons/md";
import { useChangeStatusMutation, useGetAllRegistrationLinkQuery } from "src/services/RegistrationLinkServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import AddRegistrationLinkFormWrapper from "../Add/AddRegistrationLinkFormWrapper";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import { showToast } from "src/utils/showToaster";
import ATMCopyToClipboard from "src/components/UI/atoms/ATMCopyToClipboard/ATMCopyToClipboard";
import { IoCopyOutline } from "react-icons/io5";
import { REGISTRATION_FORM_URL } from "src/utils/constants";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const RegistrationLinkListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const [isOpenAddForm, setIsOpenAddForm] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.registrationLink);

  const [changeStatus] = useChangeStatusMutation()
  const { data, isLoading, isFetching } = useGetAllRegistrationLinkQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["registrationLink"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (linkId: string) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center  font-semibold">
            <MdOutlinePublishedWithChanges className="text-lg" /> Change Status
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to change the status of this link?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                changeStatus(linkId).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.CHANGE_STATUS,
        moduleName: UserModuleNameTypes.REGISTRATION_LINKS,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "createdAt",
      headerName: "Date - Time",
      extraClasses: "min-w-[100px] py-1 max-w-[150px]",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium text-base">
            {formatedDateTimeIntoIst(row.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[12px] font-medium text-slate-400">
            {formatedDateTimeIntoIst(row.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },

    {
      field: "registrationLink",
      headerName: "Registration Link",
      flex: "flex-[1.5_1.5_0%]",
      align: 'center',
      renderCell: (row: any) => (
        <div
          onClick={(e: any) => {
            e.stopPropagation();
          }}
          className="bg-stone-100 p-[6px] flex justify-between items-center rounded-xl  text-[13px] gap-4 cursor-defaul"
        >
          Copy Link
          <ATMCopyToClipboard
            tooltipTitle={isCopied ? "Copied" : "Copy"}
            copyText={`${REGISTRATION_FORM_URL}/registration/${row?.registrationLink}`}
            onCopy={() => {
              setIsCopied(true);
              setTimeout(() => {
                setIsCopied(false);
              }, 1000);
            }}
          >
            <IoCopyOutline className="cursor-pointer" />
          </ATMCopyToClipboard>
        </div>
      ),
    },
    { field: "batchName", headerName: "Batch Name", flex: "flex-[1_1_0%]" },
    {
      field: "noOfRegistration",
      headerName: "Number Of Registrations",
      flex: "flex-[1_1_0%]",
      align: 'center',
    },
    { field: "createdByName", headerName: "Create By", flex: "flex-[1_1_0%]" },
    {
      field: "isActive",
      headerName: "isActive",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {

        return (
          <div>
            {row?.isActive ? <span className='text-green-700 font-medium'>Yes</span> : <span className="font-medium text-red-700">No</span>}
          </div>
        );
      },
    },
    {
      noAuthRequired: true,
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      extraClasses: " py-2",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];
  const CustomArray = [UserModuleActionTypes.CHANGE_STATUS]

  return (
    <div>
      <RegistrationLinkListing
        columns={
          isCheckColumnPermission({
            moduleName: UserModuleNameTypes.REGISTRATION_LINKS,
            actionName: UserModuleActionTypes.LIST,
            columns: columns,
            actionArray: CustomArray
          })}
        rows={items}
        onAddNew={() => setIsOpenAddForm(true)}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
      {isOpenAddForm && (
        <AddRegistrationLinkFormWrapper
          onClose={() => {
            setIsOpenAddForm(false);
          }}
        />
      )}
    </div>
  );
};

export default RegistrationLinkListingWrapper;
